import {
  useContext,
  useState,
  useEffect,
  useCallback,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import ContractContext, { useSchema } from "../ContractContext";
import FormGroup from "../../forms/FormGroup";
import { LabeledInputComponent } from "../../forms/LabeledInputComponent";
import {
  useInputValidation,
  ValidationParameters,
} from "../validation/InputValidationContext";
import ErrorMessage from "../../shared/error/ErrorMessage";
import { LabelHint } from "./LabelHint";
import { ContractViewModel, FieldValue } from "../model/ContractViewModel";
import {
  FieldInput,
  FieldType,
  FieldValueProps,
  FieldValueType,
} from "./FieldInput";
import { DateString } from "@vehicle-contract/common/lib/model/common/DateString";

export interface FieldSpecification {
  section: keyof ContractViewModel;
  name: string;
}

interface ContractFieldErrorProps extends FieldSpecification {}

export function ContractFieldError({ section, name }: ContractFieldErrorProps) {
  const { error } = useInputValidation(section, name);

  if (!error) return null;

  if (error.isRequired) {
    return <ErrorMessage>Feltet er påkrevd.</ErrorMessage>;
  }

  if (error.maxLines) {
    return (
      <ErrorMessage>
        Det er begrenset med plass i kontrakten. Sjekk forhåndsvisningen og pass
        på at alt kommer med.
      </ErrorMessage>
    );
  }

  if (error.patternError) {
    return <ErrorMessage>{error.patternError}</ErrorMessage>;
  }

  if (error.callbackError) {
    return <ErrorMessage>{error.callbackError}</ErrorMessage>;
  }

  return null;
}

export interface ContractFieldProps extends ContractFieldInputProps {
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  description?: string;
  labelHint?: string;
  type: FieldType;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
}

export function ContractField({
  section,
  name,
  type,
  label,
  required,
  disabled,
  description,
  labelHint,
  inputProps,
  ...rest
}: ContractFieldProps) {
  return (
    <ContractFieldWrapper
      section={section}
      name={name}
      type={type}
      label={label}
      disabled={disabled}
      required={required}
      labelHint={labelHint}
    >
      <ContractFieldInput
        section={section}
        name={name}
        type={type}
        inputProps={{
          ...inputProps,
          disabled: disabled || inputProps?.disabled,
          required: required || inputProps?.required,
        }}
        {...rest}
      />
    </ContractFieldWrapper>
  );
}

export interface ContractFieldWrapperProps extends FieldSpecification {
  type: FieldType;
  children: ReactNode;
  label?: ReactNode;
  required?: boolean;
  labelHint?: string;
  disabled?: boolean;
  hasError?: boolean;
}

export const ContractFieldWrapper = ({
  section,
  name,
  children,
  label: overrideLabel,
  required: overrideRequired,
  labelHint: overrideLabelHint,
  disabled,
  hasError,
}: ContractFieldWrapperProps) => {
  const { error } = useInputValidation(section, name);
  const schema = useSchema();
  const { label, description } = schema.getFieldType(section, name);
  const { required: schemaRequired } = schema.getFieldValidation(section, name);
  const required =
    overrideRequired !== undefined && overrideRequired !== null
      ? overrideRequired
      : schemaRequired;

  return (
    <FormGroup className={`ContractField-${name}`} data-name={name}>
      <LabeledInputComponent
        id={`${section}.${name}`}
        label={
          <>
            <span>{overrideLabel || label}</span>
            {overrideLabelHint ? (
              <LabelHint> {overrideLabelHint}</LabelHint>
            ) : required ? (
              <LabelHint> (påkrevd)</LabelHint>
            ) : null}
          </>
        }
        description={description as string}
        hasError={hasError || !!error}
        disabled={disabled}
      >
        {children}
        <ContractFieldError section={section} name={name} />
      </LabeledInputComponent>
    </FormGroup>
  );
};

export interface ContractFieldInputProps extends FieldSpecification {
  type: FieldType;
  required?: boolean;
  mask?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  validation?: ValidationParameters;
}

export interface ContractFieldParameters extends FieldSpecification {
  type: FieldType | "checkbox";
  validation?: ValidationParameters;
}

export function useContractField<T extends FieldValueType>({
  type,
  section,
  name,
  validation,
}: ContractFieldParameters): FieldValueProps<T> {
  const toFieldValue = useCallback(
    function (value: FieldValueType): FieldValue {
      switch (type) {
        case "date":
          return { dateTimeValue: value as DateString };
        case "text":
        case "account":
        case "email":
        case "tel":
        case "phone":
        case "textarea":
          return { stringValue: value as string };
        case "decimal":
          return { decimalValue: value as number };
        case "int":
          return { intValue: value as number };
        case "checkbox":
        case "boolean":
          return { boolValue: value as boolean };
        case "intString":
          return { stringValue: value as string };
        default:
          throw new Error(`Unrecognized type ${type}`);
      }
    },
    [type]
  );
  const contractContext = useContext(ContractContext);
  if (!contractContext) throw new Error("No ContractContext found!");
  const { contract, saveFields, enqueueChanges } = contractContext;
  const group = (contract || {})[section] || {};
  const savedValueInner = (group as any)[name] as T | undefined;
  const savedValue =
    typeof savedValueInner === "undefined" ? null : savedValueInner;
  const [current, setCurrent] = useState<T | null>(savedValue);
  const { validate } = useInputValidation(section, name, validation);

  useEffect(() => {
    setCurrent(savedValue);
  }, [savedValue]);

  const onSave = useCallback(
    async function save(value) {
      validate(value);

      const saves =
        savedValue === value
          ? []
          : [
              {
                section,
                field: name,
                value,
                fieldValue: value !== null ? toFieldValue(value) : null,
              },
            ];

      await saveFields(saves);
    },
    [validate, saveFields, section, name, toFieldValue, savedValue]
  );

  const onChange = useCallback(
    function onChange(value: T | null) {
      if (savedValue !== value) {
        enqueueChanges([
          {
            section,
            field: name,
            value: value,
            fieldValue: value !== null ? toFieldValue(value) : null,
          },
        ]);
      }
      setCurrent(value);
    },
    [section, name, toFieldValue, enqueueChanges, savedValue]
  );

  return {
    current,
    onChange,
    onSave,
  };
}

export function ContractFieldInput({
  section,
  name,
  type,
  validation,
  inputProps,
  ...rest
}: ContractFieldInputProps) {
  const value = useContractField<any>({ section, name, type, validation });

  return (
    <FieldInput
      section={section}
      name={name}
      type={type}
      value={value}
      required={validation?.required}
      inputProps={inputProps}
      {...rest}
    />
  );
}
